// Die Facetten der Kontaktsuche (ST-SEA-001, CM-SEA-003): der zugeklappte
// Filterbereich und die Chips der angewandten Filter.
//
// PROGRESSIVE OFFENLEGUNG (5.2 Nr. 9): Der Bereich ist zu, bis jemand
// "Filter" waehlt; die Hauptaktion bleibt "Suchen" im Formular darueber und
// gilt fuer Begriff und Filter zusammen. Gesucht wird beim Abschicken, nicht
// beim Aendern eines Kaestchens -- dieselbe Regel wie beim Begriff (ADR-0049).
//
// AKTIVE FILTER SIND JEDERZEIT SICHTBAR (CM-SEA-003): als Chips ueber der
// Liste, auch die Voreinstellung "ohne Inaktive" -- ein unsichtbarer Filter
// waere eine Liste, die weniger zeigt, als die Nutzerin erwartet. Einen Chip
// zu entfernen sucht sofort neu; das ist die direkte Handlung, die der Chip
// verspricht.
//
// KEINE ZWEITE STATUSREGION: der Bereich meldet Ladefehler als Text am Feld,
// die einzige Region, die spricht, bleibt "Status: Kontakte".

import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Body1,
  Button,
  Checkbox,
  Dropdown,
  Field,
  Input,
  makeStyles,
  Option,
} from '@fluentui/react-components';

import { Symbol } from '../shell/symbole.tsx';
import { artWort, auswahlWort, gemeinsameArt } from '../haus-art.ts';
import type { PlainMessage } from '../access.ts';
import { fetchAssignableTerms, truncationNotice, type AssignableTermView } from '../classifications.ts';
import { mitAnmeldung } from '../shell/anmeldung.ts';
import { ROLE_LABELS, roleLabel } from '../context-roles.ts';
import { uebernimmHausArten } from '../suche/abfrage/felder.ts';
import { searchFacetTerms } from '../suche/aufloesung.ts';
import { brauchtSuchfeld, StichwortSuchfeld, type StichwortTreffer } from '../suche/stichwort-suchfeld.tsx';
import {
  activeFilterCount,
  type AddressFilterProblems,
  fetchSearchOptions,
  MAXIMUM_FILTER_TERMS,
  MAXIMUM_POSTAL_CODE_PREFIX_LENGTH,
  type SearchFilter,
  type SearchOptionContext,
} from '../parties.ts';

/** Lesende Abrufe erneuern eine abgelaufene Anmeldung still und wiederholen einmal. */
const LESEN = mitAnmeldung();

const useStyles = makeStyles({
  bereich: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '0.75rem',
  },
  anschrift: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  plz: {
    width: '9rem',
    // Das Raster des Felds bekommt die Spaltenbreite statt der des breitesten Kinds
    // (das Eingabefeld ist von sich aus 234 px breit); sonst ragt der Hinweis ueber die 9 rem.
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  ort: {
    flexGrow: 1,
    // Bei 320 px und 200 % Text sind 12 rem breiter als die Zeile (WCAG 1.4.10).
    minWidth: 'min(12rem, 100%)',
    // Wie bei der PLZ: das Eingabefeld bekommt die Spaltenbreite, sonst ragt es mit
    // seinen 234 px in den Innenrand der Karte.
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  eingabe: {
    minWidth: 0,
  },
  // Fluents Dropdown haelt 250 px Mindestbreite; schmal mit grosser Schrift ragte
  // er ueber den Fensterrand. Das Feld gibt ihm die Spaltenbreite.
  auswahlFeld: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  auswahl: {
    minWidth: 0,
  },
  rollen: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: '1rem',
  },
  chips: {
    listStyle: 'none',
    margin: '0.75rem 0 0',
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  // Chipzeile und „Als Suchtext kopieren“ brechen gemeinsam um; der Knopf
  // gehoert nicht in die Liste, sonst zaehlte er als Filter.
  verborgen: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  chipZeile: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '0.5rem',
  },
  hinweis: {
    overflowWrap: 'anywhere',
  },
  // Zweizeilig steht der Chip buendig links wie jeder Text, nicht mittig wie Fluents Knopf (Restliste B7).
  chip: { textAlign: 'start' },
  // Ein langer Organisationsname kuerzt im Chip per CSS, nicht im Text (DESIGN zu E3 S2b);
  // auf schmalem Schirm bricht er um, statt abzuschneiden. Der zugaengliche Name traegt ihn ganz.
  chipName: {
    display: 'inline-block',
    maxWidth: '20rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
    // Schmal fliesst der Name im Satz und bricht an Wortgrenzen; anywhere zerteilte ihn Buchstabe fuer Buchstabe.
    '@media (max-width: 30rem)': { display: 'inline', maxWidth: 'none', whiteSpace: 'normal', overflowWrap: 'break-word' },
  },
  // Der Hinweis eines Felds ist ein Rasterkind mit min-width: auto und bliebe so breit wie sein
  // Satz; er soll im Feld umbrechen, nicht in den Nachbarn ragen.
  feldHinweis: {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
});

export const FILTER_PANEL_ID = 'kontakte-filter';
export const FILTER_TOGGLE_ID = 'kontakte-filter-knopf';
export const FILTER_POSTAL_CODE_ID = 'kontakte-filter-plz';
export const FILTER_LOCALITY_ID = 'kontakte-filter-ort';

/** Die Rollencodes in der Reihenfolge der Tabelle -- dieselbe geschlossene Liste wie der Server (0042). */
const ROLE_CODES = Object.keys(ROLE_LABELS);

export type OptionenStatus = 'ruht' | 'laedt' | 'geladen' | 'abgelehnt';

export interface FilterOptionen {
  readonly status: OptionenStatus;
  readonly begriffe: readonly AssignableTermView[];
  readonly gekuerzt: boolean;
  readonly haeuser: readonly SearchOptionContext[];
  readonly fehler?: PlainMessage | undefined;
  readonly laden: () => Promise<void>;
  /** Die Serversuche der Facette ab 21 Begriffen: S0, dieselbe Regel wie die Suche. */
  readonly stichwortSuchen: (getippt: string, signal: AbortSignal) => Promise<StichwortTreffer>;
}

/** Laedt Stichwoerter und eigene Haeuser einmal, beim ersten Bedarf. */
export function useFilterOptionen(
  apiBaseUrl: string,
  accessToken: () => Promise<string | undefined>,
  onReauthenticate: () => void,
): FilterOptionen {
  const [status, setStatus] = useState<OptionenStatus>('ruht');
  const [begriffe, setBegriffe] = useState<readonly AssignableTermView[]>([]);
  const [gekuerzt, setGekuerzt] = useState(false);
  const [haeuser, setHaeuser] = useState<readonly SearchOptionContext[]>([]);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  // Der Zustand im Closure kann veraltet sein, wenn Effekt und Klick kurz
  // hintereinander laden wollen; der Ref sieht den laufenden Abruf sofort.
  const laeuft = useRef(false);

  const laden = useCallback(async (): Promise<void> => {
    if (laeuft.current || status === 'geladen') {
      return;
    }
    laeuft.current = true;
    setStatus('laedt');
    setFehler(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        // Nicht zurueck auf "ruht": der Bildschirm laedt aus dem Ruhezustand
        // selbsttaetig, und die Anmeldung laeuft ohnehin neu.
        onReauthenticate();
        setStatus('abgelehnt');
        return;
      }
      const [terms, options] = await Promise.all([
        fetchAssignableTerms({ baseUrl: apiBaseUrl, accessToken: token, send: LESEN }),
        fetchSearchOptions({ baseUrl: apiBaseUrl, accessToken: token, send: LESEN }),
      ]);
      if (terms.outcome === 'rejected' || options.outcome === 'rejected') {
        setFehler(terms.outcome === 'rejected' ? terms.message : options.outcome === 'rejected' ? options.message : undefined);
        setStatus('abgelehnt');
        return;
      }
      setBegriffe(terms.terms);
      setGekuerzt(terms.truncated);
      setHaeuser(options.operatingContexts);
      uebernimmHausArten(options.operatingContexts.map((haus) => haus.kind));
      setStatus('geladen');
    } catch {
      setFehler({
        headline: 'Die Filter konnten nicht geladen werden.',
        explanation: 'Es kam keine Antwort.',
        nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
        retryable: true,
      });
      setStatus('abgelehnt');
    } finally {
      laeuft.current = false;
    }
  }, [status, accessToken, apiBaseUrl, onReauthenticate]);

  const stichwortSuchen = useCallback(
    (getippt: string, signal: AbortSignal): Promise<StichwortTreffer> =>
      searchFacetTerms({ baseUrl: apiBaseUrl, accessToken, onReauthenticate, query: getippt, signal, send: LESEN }),
    [accessToken, apiBaseUrl, onReauthenticate],
  );

  return { status, begriffe, gekuerzt, haeuser, fehler, laden, stichwortSuchen };
}

/**
 * Solange die Namen fehlen, sagt der Chip das, statt einen leeren Namen zu
 * wiederholen. Ein im Suchfeld getipptes Stichwort bringt seinen Namen aus der
 * Aufloesung mit, auch wenn die Auswahl ihn nicht kennt.
 */
function begriffName(optionen: FilterOptionen, filter: SearchFilter, id: string): string {
  return optionen.begriffe.find((begriff) => begriff.taxonomyTermId === id)?.displayName ?? filter.termNames?.[id]?.name ?? '(Name wird geladen)';
}

function hausName(optionen: FilterOptionen, id: string): string {
  return optionen.haeuser.find((haus) => haus.operatingContextId === id)?.displayName ?? '(Name wird geladen)';
}

/** "im gewählten Verlag", "in der gewählten Holding", gemischt "in der gewählten Gesellschaft" (M7). */
function imGewaehlten(haeuser: readonly SearchOptionContext[]): string {
  const art = gemeinsameArt(haeuser.map((haus) => haus.kind));
  if (art === 'gemischt' || art === 'holding') return `in der gewählten ${art === 'holding' ? 'Holding' : 'Gesellschaft'}`;
  return `im gewählten ${artWort(art, 'nom')}`;
}

/** "Alle meine Verlage", gemischt "Alle meine Gesellschaften" (M7). */
function alleMeine(haeuser: readonly SearchOptionContext[]): string {
  const art = gemeinsameArt(haeuser.map((haus) => haus.kind));
  return `Alle meine ${art === 'gemischt' ? 'Gesellschaften' : artWort(art, 'plural')}`;
}

/** Ein Anfang zeigt mit den Auslassungspunkten, dass mehr folgt; eine volle PLZ steht ohne. */
function plzText(plz: string): string {
  return plz.length < MAXIMUM_POSTAL_CODE_PREFIX_LENGTH ? `${plz}…` : plz;
}

/**
 * Der Knopf, der den Bereich auf- und zuklappt; er nennt die Zahl der gesetzten
 * Filter. Ein Nebenknopf mit Symbol rechts am Feld (DESIGN L14-03), kein
 * fetter Text. `textKlasse` blendet die Beschriftung am schmalen Feld aus; der
 * Name bleibt dann ueber aria-label derselbe.
 */
export function FilterKnopf(props: {
  readonly offen: boolean;
  readonly entwurf: SearchFilter;
  readonly onUmschalten: () => void;
  readonly textKlasse?: string | undefined;
  readonly className?: string | undefined;
}): ReactElement {
  const zahl = activeFilterCount(props.entwurf);
  const name = zahl === 0 ? 'Filter' : `Filter (${zahl})`;
  return (
    <Button
      id={FILTER_TOGGLE_ID}
      type="button"
      appearance="secondary"
      className={props.className}
      icon={<Symbol name="filter" />}
      aria-label={name}
      aria-expanded={props.offen}
      aria-controls={FILTER_PANEL_ID}
      onClick={props.onUmschalten}
    >
      <span className={props.textKlasse}>{name}</span>
    </Button>
  );
}

/** Der aufgeklappte Bereich: Stichwort, Verlag bzw. Gesellschaft (nur bei mehr als einem), Rolle, PLZ/Ort, Inaktive. */
export function FilterBereich(props: {
  readonly entwurf: SearchFilter;
  readonly onEntwurf: (next: SearchFilter) => void;
  readonly optionen: FilterOptionen;
  /** Formfehler von PLZ und Ort aus dem letzten "Suchen"; sie stehen am Feld. */
  readonly fehler: AddressFilterProblems;
  /** Weicht der Entwurf von der angewandten Suche ab? Dann sagt der Bereich, dass "Suchen" noch fehlt. */
  readonly ungesendet: boolean;
}): ReactElement {
  const own = useStyles();
  const { entwurf, onEntwurf, optionen } = props;
  const gewaehlteBegriffe = entwurf.taxonomyTermIds;
  const [voll, setVoll] = useState(false);

  return (
    <div id={FILTER_PANEL_ID} role="group" aria-label="Filter" className={own.bereich}>
      <Body1>Alle gesetzten Filter müssen zutreffen; bei mehreren Stichworten oder Rollen genügt eines.</Body1>

      {/* Nur beim Fehler vorhanden und dann einmal angesagt: der Fokus bleibt
          auf "Filter", und ohne Ansage hoerte ein Screenreader den Satz erst
          beim Durchlesen. Rolle und Inaktive
          bleiben bedienbar. */}
      {optionen.status === 'abgelehnt' && optionen.fehler !== undefined ? (
        <Body1 role="alert" className={own.hinweis}>
          {optionen.fehler.headline} {optionen.fehler.nextStep}{' '}
          <Button type="button" appearance="secondary" size="small" onClick={() => void optionen.laden()}>
            Filter erneut laden
          </Button>
        </Body1>
      ) : undefined}

      {optionen.status === 'geladen' && brauchtSuchfeld(optionen.begriffe.length, optionen.gekuerzt) ? (
        <StichwortSuchfeld
          label="Stichwort"
          hinweis={voll ? `Höchstens ${MAXIMUM_FILTER_TERMS} Stichworte; entferne eines, um ein anderes zu wählen.` : undefined}
          ersteSeite={optionen.begriffe.map((b) => ({ id: b.taxonomyTermId, name: b.displayName, achse: b.taxonomyDisplayName }))}
          ersteSeiteGekuerzt={optionen.gekuerzt}
          suchen={optionen.stichwortSuchen}
          gewaehlt={gewaehlteBegriffe.map((id) => ({
            id,
            name: begriffName(optionen, entwurf, id),
            achse: optionen.begriffe.find((b) => b.taxonomyTermId === id)?.taxonomyDisplayName ?? entwurf.termNames?.[id]?.axis ?? '',
          }))}
          abgelehntSatz={`Höchstens ${MAXIMUM_FILTER_TERMS} Stichworte; entferne eines, um ein anderes zu wählen.`}
          onHinzufuegen={(eintrag) => {
            if (gewaehlteBegriffe.length >= MAXIMUM_FILTER_TERMS) {
              setVoll(true);
              return false;
            }
            setVoll(false);
            onEntwurf({
              ...entwurf,
              taxonomyTermIds: [...gewaehlteBegriffe, eintrag.id],
              // Ein gesuchter Begriff steht nicht in der ersten Seite: sein Name reist mit.
              termNames: { ...entwurf.termNames, [eintrag.id]: { name: eintrag.name, axis: eintrag.achse } },
            });
            return true;
          }}
          onEntfernen={(id) => {
            setVoll(false);
            onEntwurf({ ...entwurf, taxonomyTermIds: gewaehlteBegriffe.filter((g) => g !== id) });
          }}
        />
      ) : (
      <Field
        className={own.auswahlFeld}
        label="Stichwort"
        hint={
          voll
            ? `Höchstens ${MAXIMUM_FILTER_TERMS} Stichworte; entferne eines, um ein anderes zu wählen.`
            : optionen.status === 'laedt' || optionen.status === 'ruht'
            ? 'Stichworte werden geladen.'
            : optionen.status === 'abgelehnt'
            ? 'Stichworte sind gerade nicht verfügbar. Lade die Filter erneut.'
            : optionen.begriffe.length === 0
            ? 'Es gibt noch keine Stichworte.'
            : optionen.gekuerzt
            ? truncationNotice(optionen.begriffe.length)
            : null
        }
      >
        <Dropdown
          className={own.auswahl}
          multiselect
          placeholder="Kein Stichwort gewählt"
          disabled={optionen.status !== 'geladen' || optionen.begriffe.length === 0}
          selectedOptions={[...gewaehlteBegriffe]}
          value={gewaehlteBegriffe.map((id) => begriffName(optionen, entwurf, id)).join(', ')}
          // Ueber der Grenze des Servers wird die Auswahl nicht angenommen;
          // der Feldhinweis nennt dann die Grenze, statt dass die Suche scheitert.
          onOptionSelect={(_event, data) => {
            if (data.selectedOptions.length > MAXIMUM_FILTER_TERMS) {
              setVoll(true);
              return;
            }
            setVoll(false);
            onEntwurf({ ...entwurf, taxonomyTermIds: data.selectedOptions });
          }}
        >
          {optionen.begriffe.map((begriff) => (
            <Option key={begriff.taxonomyTermId} value={begriff.taxonomyTermId} text={begriff.displayName}>
              {`${begriff.displayName} (${begriff.taxonomyDisplayName})`}
            </Option>
          ))}
        </Dropdown>
      </Field>
      )}

      {optionen.haeuser.length > 1 ? (
        // Das Feld heisst nach der Art der eigenen Haeuser, gemischt "Gesellschaft" (M7); die Auswahl zeigt die Namen.
        <Field className={own.auswahlFeld} label={auswahlWort(optionen.haeuser.map((haus) => haus.kind))} hint={`Nur Kontakte mit einer Rolle ${imGewaehlten(optionen.haeuser)}.`}>
          <Dropdown
            className={own.auswahl}
            placeholder={alleMeine(optionen.haeuser)}
            selectedOptions={entwurf.operatingContextId === undefined ? ['alle'] : [entwurf.operatingContextId]}
            value={entwurf.operatingContextId === undefined ? alleMeine(optionen.haeuser) : hausName(optionen, entwurf.operatingContextId)}
            onOptionSelect={(_event, data) =>
              onEntwurf({ ...entwurf, operatingContextId: data.optionValue === 'alle' ? undefined : data.optionValue })
            }
          >
            <Option value="alle">{alleMeine(optionen.haeuser)}</Option>
            {optionen.haeuser.map((haus) => (
              <Option key={haus.operatingContextId} value={haus.operatingContextId}>
                {haus.displayName}
              </Option>
            ))}
          </Dropdown>
        </Field>
      ) : undefined}

      <Field label="Rolle">
        <div className={own.rollen}>
          {ROLE_CODES.map((code) => (
            <Checkbox
              key={code}
              label={roleLabel(code)}
              checked={entwurf.roleCodes.includes(code)}
              onChange={(_event, data) =>
                onEntwurf({
                  ...entwurf,
                  // In der Reihenfolge der Tabelle, damit derselbe Filter immer gleich aussieht.
                  roleCodes: ROLE_CODES.filter((c) => (c === code ? data.checked === true : entwurf.roleCodes.includes(c))),
                })
              }
            />
          ))}
        </div>
      </Field>

      {/* Freitext statt Auswahl: PLZ und Ort sind offene Werte. Kein
          Autovervollstaendigen durch den Browser -- er wuerde Anschriften,
          also Personendaten, auf dem Geraet merken. */}
      <div className={own.anschrift}>
        <Field
          className={own.plz}
          label="PLZ (Anfang)"
          hint={{ children: 'Z. B. 80 oder 80331.', className: own.feldHinweis }}
          validationMessage={props.fehler.postalCodePrefix ?? null}
          validationState={props.fehler.postalCodePrefix === undefined ? 'none' : 'error'}
        >
          <Input
            id={FILTER_POSTAL_CODE_ID}
            inputMode="numeric"
            autoComplete="off"
            // Keine Laengengrenze an beiden Feldern: sie schnitte Eingefuegtes
            // wie "80331 München" still ab; die Pruefung vor dem Senden sagt es.
            value={entwurf.postalCodePrefix}
            onChange={(_event, data) => onEntwurf({ ...entwurf, postalCodePrefix: data.value })}
          />
        </Field>
        <Field
          className={own.ort}
          label="Ort"
          hint={{ children: 'Der ganze Ortsname; ob mit ä oder ae geschrieben, ist egal.', className: own.feldHinweis }}
          validationMessage={props.fehler.locality ?? null}
          validationState={props.fehler.locality === undefined ? 'none' : 'error'}
        >
          <Input
            className={own.eingabe}
            id={FILTER_LOCALITY_ID}
            autoComplete="off"
            value={entwurf.locality}
            onChange={(_event, data) => onEntwurf({ ...entwurf, locality: data.value })}
          />
        </Field>
      </div>

      <Checkbox
        label="Inaktive Kontakte einbeziehen"
        checked={entwurf.includeInactive}
        onChange={(_event, data) => onEntwurf({ ...entwurf, includeInactive: data.checked === true })}
      />
      {props.ungesendet ? <Body1 className={own.hinweis}>Änderungen gelten nach „Suchen“.</Body1> : undefined}
    </div>
  );
}

/**
 * Die angewandten Filter als Chips. `onEntfernen` bekommt die Aenderung als
 * Funktion: der Bildschirm wendet sie auf den angewandten Filter UND auf den
 * Entwurf an, damit ungesendete Haekchen im Bereich nicht verloren gehen.
 */
export function AktiveFilter(props: {
  readonly filter: SearchFilter;
  readonly optionen: FilterOptionen;
  readonly onEntfernen: (ohne: (filter: SearchFilter) => SearchFilter) => void;
  /** Die Angaben als Suchtext (PQL S1); fehlt er, steht kein Kopierknopf. */
  readonly suchtext?: string | undefined;
  /** Die Zwischenablage hat nicht angenommen: der Bildschirm setzt den Text ins Feld. */
  readonly onKopierenGescheitert?: ((text: string) => void) | undefined;
  /** Steht direkt hinter den Chips, etwa der Knopf „Filter“ (DESIGN P3 zu #524). */
  readonly danach?: ReactNode;
}): ReactElement {
  const own = useStyles();
  const { filter, optionen, onEntfernen, suchtext, onKopierenGescheitert } = props;
  // „Kopiert“ bleibt bis zur naechsten Aenderung der Angaben (DESIGN, ohne Zeitlimit).
  const [kopierStand, setKopierStand] = useState<'bereit' | 'kopiert' | 'gescheitert'>('bereit');
  useEffect(() => setKopierStand('bereit'), [suchtext]);
  const kopieren = (): void => {
    if (suchtext === undefined) return;
    const scheitern = (): void => {
      setKopierStand('gescheitert');
      onKopierenGescheitert?.(suchtext);
    };
    try {
      void (navigator.clipboard?.writeText(suchtext) ?? Promise.reject(new Error('keine Zwischenablage'))).then(() => setKopierStand('kopiert'), scheitern);
    } catch {
      scheitern();
    }
  };
  const chips: { readonly wert: string; readonly text: string; readonly name?: string }[] = [
    ...filter.taxonomyTermIds.map((id) => ({ wert: `stichwort:${id}`, text: `Stichwort: ${begriffName(optionen, filter, id)}` })),
    ...(filter.operatingContextId === undefined
      ? []
      : [{ wert: 'haus', text: `${artWort(optionen.haeuser.find((haus) => haus.operatingContextId === filter.operatingContextId)?.kind, 'nom')}: ${hausName(optionen, filter.operatingContextId)}` }]),
    ...filter.roleCodes.map((code) => ({ wert: `rolle:${code}`, text: `Rolle: ${roleLabel(code)}` })),
    ...(filter.postalCodePrefix.trim().length === 0 ? [] : [{ wert: 'plz', text: `PLZ: ${plzText(filter.postalCodePrefix.trim())}` }]),
    ...(filter.locality.trim().length === 0 ? [] : [{ wert: 'ort', text: `Ort: ${filter.locality.trim()}` }]),
    ...(filter.contactPersonOf === undefined ? [] : [{ wert: 'ansprechpartner', text: `Ansprechpartner:innen von ${filter.contactPersonOf.displayName}`, name: filter.contactPersonOf.displayName }]),
    ...(filter.includeInactive ? [] : [{ wert: 'ohne-inaktive', text: 'ohne Inaktive' }]),
  ];

  const entfernen = (wert: string): void => {
    if (wert === 'haus') {
      onEntfernen((f) => ({ ...f, operatingContextId: undefined }));
    } else if (wert === 'plz') {
      onEntfernen((f) => ({ ...f, postalCodePrefix: '' }));
    } else if (wert === 'ort') {
      onEntfernen((f) => ({ ...f, locality: '' }));
    } else if (wert === 'ansprechpartner') {
      onEntfernen((f) => ({ ...f, contactPersonOf: undefined }));
    } else if (wert === 'ohne-inaktive') {
      onEntfernen((f) => ({ ...f, includeInactive: true }));
    } else if (wert.startsWith('stichwort:')) {
      onEntfernen((f) => ({ ...f, taxonomyTermIds: f.taxonomyTermIds.filter((id) => `stichwort:${id}` !== wert) }));
    } else if (wert.startsWith('rolle:')) {
      onEntfernen((f) => ({ ...f, roleCodes: f.roleCodes.filter((code) => `rolle:${code}` !== wert) }));
    }
  };

  // Eine Liste kleiner Knoepfe statt Fluents entfernbarer Tags: der Name ist
  // "<Filter> entfernen" und enthaelt den sichtbaren Text (WCAG 2.5.3), das
  // Kreuz ist Schmuck. Der entfernbare Tag machte den ganzen Chip zum Knopf,
  // verdoppelte den Namen und trug versteckte fokussierbare Elemente (axe
  // aria-hidden-focus, Probe F1).
  return (
    <div className={own.chipZeile}>
      <ul aria-label="Aktive Filter" className={own.chips}>
        {chips.map((chip) => (
          <li key={chip.wert}>
            <Button type="button" size="small" appearance="outline" className={own.chip} aria-label={`${chip.text} entfernen`} onClick={() => entfernen(chip.wert)}>
              {chip.name === undefined ? (
                chip.text
              ) : (
                // Ein gemeinsamer Span: als Flex-Kind des Knopfs verloere der Text sein Leerzeichen vor dem Namen.
                <span>
                  {chip.text.slice(0, chip.text.length - chip.name.length)}
                  <span className={own.chipName}>{chip.name}</span>
                </span>
              )}
              <span aria-hidden="true">&nbsp;✕</span>
            </Button>
          </li>
        ))}
      </ul>
      {props.danach}
      {suchtext !== undefined && chips.some((chip) => chip.wert !== 'ohne-inaktive') ? (
        <span>
          <Button type="button" size="small" appearance="transparent" onClick={kopieren}>
            {kopierStand === 'kopiert' ? 'Kopiert' : 'Als Suchtext kopieren'}
          </Button>
          {/* Eigene kleine Region: die Statusregion der Ergebnisse traegt die Trefferzahl. */}
          <span aria-live="polite">
            {kopierStand === 'kopiert' ? <span className={own.verborgen}>Suchtext kopiert.</span> : undefined}
            {kopierStand === 'gescheitert' ? ' Kopieren ging nicht. Der Suchtext steht jetzt im Feld.' : undefined}
          </span>
        </span>
      ) : undefined}
    </div>
  );
}
